/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { TextField } from 'shared/components/form/Inputs';
import { removeBreadcrumb } from 'redux/actions/layoutActions';
import { setClientsCount } from 'redux/actions/resultActions';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Inject,
  Filter,
  Resize,
  Sort,
} from '@syncfusion/ej2-react-grids';

import { getBestFontContrast, capitalizeFirstLetter } from 'shared/utils';

import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons';
import { MultiSelect, CheckBoxSelection } from '@syncfusion/ej2-react-dropdowns';

import useApi from 'hooks/useApi';
import { colors } from 'shared/defaultTheme';
import { L10n, createElement, setCulture } from '@syncfusion/ej2-base';
import { TextBox } from '@syncfusion/ej2-react-inputs';
import MainSpinner from 'shared/components/MainSpinner';
import { ClientResultsWrapper } from '../styles';
import { L10nObj } from '../contants';

MultiSelect.Inject(CheckBoxSelection);

const ClientResults = forwardRef(({ tagsList, statesList, fullStates, getCounters }, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { customAreaApi } = useApi();
  const history = useHistory();
  const { breadcrumbs } = useSelector((state) => state.layout);
  const { cusNavId } = useSelector((state) => state.auth);
  const { generalSearchText } = useSelector((state) => state.results);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const [language, setLanguage] = useState('es');
  const [searchValue, setSearchValue] = useState('');
  const [pageSettings, setPageSettings] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState();

  const grid = useRef(null);
  let data;
  let dropInstance;

  useEffect(() => {
    const lang = selectedDefaultLanguage.slice(0, 2);
    setLanguage(lang);
    setCulture(lang);
    L10n.load(L10nObj);
  }, [selectedDefaultLanguage]);

  const [, getClients] = customAreaApi(
    {
      url: `${window.API_V1}/cli/clients/${cusNavId}/search/all`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getClientsCount] = customAreaApi(
    {
      url: `${window.API_V1}/cli/clients/${cusNavId}/search/all/count`,
      method: 'POST',
    },
    { manual: true }
  );

  const setLoader = (value) => {
    if (!document.querySelector('.e-spinner-pane')) return;
    if (value) {
      document.querySelector('.e-spinner-pane').style.display = 'block';
    } else {
      document.querySelector('.e-spinner-pane').style.display = 'none';
    }
  };

  const getFilters = (state) => {
    const filtering = state?.where?.[0].predicates || [];

    const finalFilters = [];
    filtering.forEach((x) => {
      if (x.field === 'tags') {
        const tagNames = x.value.split(',');
        const tagIds = tagsList.filter((tag) => tagNames.includes(tag.text)).map((tag) => tag.id);
        x.value = tagIds;
        finalFilters.push(x);
      }
      if (x.field === 'stateClient') {
        const stateNames = x.value.split(',');
        const stateIds = statesList.filter((s) => stateNames.includes(s.text)).map((s) => s.id);
        x.value = stateIds;
        finalFilters.push(x);
      }

      if (!['tags', 'stateClient'].includes(x.field)) {
        finalFilters.push(x);
      }
    });
    return finalFilters;
  };

  const getData = async (state) => {
    setLoader(true);
    let filtering;
    let sortingFilter = sorting;

    if (state?.action?.action === 'filter' || state?.action?.action === 'clearFilter') {
      // filtering = state?.where?.[0].predicates || [];
      filtering = getFilters(state);
      setFilters(filtering);
    }

    const currentFilters = filtering || filters;
    const dataFilters = currentFilters.map((x) => {
      const filter = {
        model: 'Client',
        field: x.field.charAt(0).toUpperCase() + x.field.slice(1),
        filter: [x.value],
        IsRegexDiacritics: true,
      };
      if (x.field === 'tags') {
        return {
          model: 'Client',
          field: 'tagsClient',
          filter: x.value,
        };
      }

      if (x.field === 'stateClient') {
        return {
          model: 'Client',
          field: 'stateClient',
          filter: x.value,
        };
      }
      return filter;
    });

    if (state?.action?.requestType === 'sorting' && state?.action?.columnName) {
      let field = '';

      if (state?.action?.columnName === 'tags') {
        field = 'TagsClient';
      } else {
        field = capitalizeFirstLetter(state?.action?.columnName);
      }

      sortingFilter = {
        model: 'Client',
        field,
        asc: state?.action?.direction === 'Ascending',
      };
    }

    if (state?.action?.requestType === 'sorting' && !state?.action?.columnName) {
      sortingFilter = null;
    }

    setSorting(sortingFilter);

    dataFilters.push(
      sortingFilter || {
        model: 'Client',
        field: 'Created',
        asc: false,
      }
    );

    const body = {
      filters: dataFilters,
      reduce: {
        limit: state.take,
        skip: state.skip / pageSize + 1,
      },
      ...((searchValue || generalSearchText) && {
        filterText: {
          regexFilter: searchValue || generalSearchText,
        },
      }),
    };

    try {
      const {
        data: { data: count },
      } = await getClientsCount({
        data: body,
      });

      const {
        data: { data: clients },
      } = await getClients({
        data: body,
      });

      const clientsData = clients.map((e) => ({
        ...e,
        tags: e?.tagsClient ? e?.tagsClient.toString() : '',
        stateClient: e?.stateClient ? e?.stateClient.toString() : '',
      }));

      dispatch(setClientsCount(count));
      setLoader(false);
      return {
        count,
        result: clientsData,
      };
    } catch (error) {
      console.error(`Error Contacts: ${error}`);
      return {
        count: 0,
        result: [],
      };
    }
  };

  const dataStateChange = async (state) => {
    setPageSize(state.take);
    const requestType = state?.action?.requestType;
    if (requestType === 'stringfilterrequest') return;
    const dataSource = await getData(state);
    if (grid.current) {
      grid.current.dataSource = dataSource;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const state = { skip: 0, take: pageSize };
      await dataStateChange(state);
    };
    setPageSettings({ pageCount: 3, pageSize, pageSizes: [5, 10, 20, 50, 75, 100] });
    fetchData();
  }, []);

  const getFilter = (field) => {
    const filter = filters.find((x) => x.field === field);
    return filter?.value || '';
  };

  const customFilter = {
    ui: {
      create: (args) => {
        document.getElementsByClassName('e-flm_optrdiv')[0].style.display = 'none';
        document.getElementsByClassName('e-flmenu-valuediv')[0].style.padding = '0';
        const flValInput = createElement('input', { className: 'flm-input' });
        args.target.appendChild(flValInput);
        dropInstance = new TextBox({
          type: 'text',
          placeholder: t('results_filter_search_value'),
          value: getFilter(args.column.field),
        });
        dropInstance.appendTo(flValInput);
      },
      read: (args) => {
        grid.current.removeFilteredColsByField(args.column.field);
        args.fltrObj.filterByColumn(args.column.field, 'contains', dropInstance.value);
      },
      write: (args) => {
        if (args.filteredValue) {
          dropInstance.value = args.filteredValue;
        }
      },
    },
  };

  const stateFilter = {
    ui: {
      create: (args) => {
        document.getElementsByClassName('e-flm_optrdiv')[0].style.display = 'none';
        document.getElementsByClassName('e-flmenu-valuediv')[0].style.padding = '0';
        const flValInput = createElement('input', { className: 'flm-input' });
        args.target.appendChild(flValInput);
        dropInstance = new MultiSelect({
          dataSource: statesList,
          fields: { text: 'text', value: 'id' },
          placeholder: t('results_filter_search_value'),
          mode: 'CheckBox',
          filtering: (e) => {
            const query = new RegExp(e.text, 'i');
            e.updateData(statesList.filter((x) => query.test(x.text)));
          },
        });
        dropInstance.appendTo(flValInput);
      },
      read: (args) => {
        args.fltrObj.filterByColumn(args.column.field, 'contains', dropInstance.text);
      },
      write: (args) => {
        dropInstance.text = args.filteredValue || '';
      },
    },
  };

  useImperativeHandle(ref, () => ({
    async doSearch() {
      const state = { skip: 0, take: pageSize };
      await getCounters();
      await dataStateChange(state);
    },
  }));

  const handleSearch = (e) => {
    if (e) {
      const { value } = e.target;
      setSearchValue(value);
    } else {
      setSearchValue('');
    }
  };

  const handleRowSelected = (row) => {
    const { data: currentClient, target } = row;
    if (target.id === 'classification-button') return;
    if (breadcrumbs.length > 1) {
      breadcrumbs.forEach(() => {
        dispatch(removeBreadcrumb());
      });
    }
    history.push(`/clients/update/${currentClient.clientId}`);
  };

  const gridClassificationTemplate = (args) => {
    let count;
    const { tagsClient } = args;
    if (!tagsClient) {
      count = 0;
    }
    const tags = tagsList?.filter((x) => tagsClient?.includes(x.id));
    count = tags.length;
    const items = tags.map((x) => {
      return {
        text: x.text,
      };
    });
    return (
      <div className={classNames('classificationWrapper')}>
        <DropDownButtonComponent cssClass="e-caret-hide" id="classification-button" items={items}>
          {count}
        </DropDownButtonComponent>
      </div>
    );
  };

  const gridStatusTemplate = (args) => {
    const { stateClient } = args;
    if (stateClient && fullStates) {
      const stateId = fullStates.find((x) => x.id === stateClient);
      if (stateId) {
        const { i18List } = stateId;
        const state = i18List?.find((x) => x.i18Code === selectedDefaultLanguage) || i18List[0];
        return (
          <div
            className="status-wrapper"
            style={{
              backgroundColor: stateId.color,
              color: getBestFontContrast(stateId.color),
            }}
          >
            {state?.i18Name}
          </div>
        );
      }
    }
    return null;
  };

  const classificationFilter = {
    ui: {
      create: (args) => {
        document.getElementsByClassName('e-flm_optrdiv')[0].style.display = 'none';
        document.getElementsByClassName('e-flmenu-valuediv')[0].style.padding = '0';
        const flValInput = createElement('input', { className: 'flm-input' });
        args.target.appendChild(flValInput);
        dropInstance = new MultiSelect({
          dataSource: tagsList,
          fields: { text: 'text', value: 'id' },
          placeholder: t('results_filter_search_value'),
          mode: 'CheckBox',
          filtering: (e) => {
            const query = new RegExp(e.text, 'i');
            e.updateData(tagsList.filter((x) => query.test(x.text)));
          },
        });
        dropInstance.appendTo(flValInput);
      },
      read: (args) => {
        args.fltrObj.filterByColumn(args.column.field, 'contains', dropInstance.text);
      },
      write: (args) => {
        dropInstance.text = args.filteredValue || '';
      },
    },
  };

  const onSearch = async () => {
    const state = { skip: 0, take: pageSize };
    await dataStateChange(state);
  };

  const onInputChange = async (e) => {
    if (e.key === 'Enter') {
      await onSearch();
    }
  };

  useEffect(() => {
    (async () => {
      if (searchValue === '') {
        const state = { skip: 0, take: pageSize };
        await dataStateChange(state);
      }
    })();
  }, [searchValue]);

  return (
    <>
      {statesList && tagsList ? (
        <ClientResultsWrapper>
          <div className="filter-container">
            <div className="form flex-column" noValidate>
              <div className="d-flex flex-row align-items-center h-50 ml-filter-section selector-filter-by-wrapper" />
              <TextField
                borderRadius={2}
                className="mb-4 m-0 h-50 filter-input"
                inputClassName="w-100"
                withButtonClear
                rightIcon={
                  <SearchIcon
                    fill={colors.black}
                    onClick={onSearch}
                    style={{ cursor: 'pointer' }}
                  />
                }
                placeholder={t('results_search_placeholder_clients')}
                value={searchValue ?? ''}
                onChange={handleSearch}
                onKeyDown={onInputChange}
              />
            </div>
          </div>

          <div className="control-pane">
            <div className="control-section">
              <GridComponent
                id="Grid"
                dataSource={data}
                ref={(g) => {
                  grid.current = g;
                }}
                allowPaging
                pageSettings={pageSettings}
                dataStateChange={dataStateChange}
                allowSorting
                allowFiltering
                filterSettings={{
                  type: 'Menu',
                  ignoreAccent: true,
                }}
                locale={language}
                rowSelected={(args) => {
                  handleRowSelected(args);
                }}
              >
                <ColumnsDirective>
                  <ColumnDirective
                    field="clientName"
                    headerTemplate={t('clients_name_column')}
                    minWidth="200"
                    width="auto"
                    textAlign="Left"
                    filter={customFilter}
                  />
                  <ColumnDirective
                    field="cif"
                    headerText={t('clients_number_id_column')}
                    width="200"
                    textAlign="Left"
                    filter={customFilter}
                  />
                  <ColumnDirective
                    field="contactPhone"
                    headerText={t('clients_mobile_column')}
                    width="140"
                    textAlign="Left"
                    filter={customFilter}
                  />
                  <ColumnDirective
                    field="direction"
                    headerText={t('clients_address_column')}
                    width="180"
                    textAlign="Left"
                    filter={customFilter}
                  />
                  <ColumnDirective
                    field="state"
                    headerText={t('clients_city_column')}
                    width="140"
                    textAlign="Left"
                    filter={customFilter}
                  />
                  {tagsList[0] !== null && (
                    <ColumnDirective
                      field="tags"
                      headerText={t('documents_search_classification')}
                      width="170"
                      textAlign="Left"
                      template={gridClassificationTemplate}
                      filter={classificationFilter}
                    />
                  )}
                  {statesList[0] !== null && (
                    <ColumnDirective
                      field="stateClient"
                      headerText={t('documents_search_status')}
                      width="120"
                      textAlign="Left"
                      template={gridStatusTemplate}
                      filter={stateFilter}
                    />
                  )}
                </ColumnsDirective>
                <Inject services={[Page, Filter, Resize, Sort]} />
              </GridComponent>
            </div>
            <div id="waitingpopup" className="waitingpopup">
              <span id="gif" className="image" />
            </div>
          </div>
        </ClientResultsWrapper>
      ) : (
        <MainSpinner />
      )}
    </>
  );
});

export default ClientResults;
